import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { getFirebaseAuth, getFirestore } from '../services/firebase'
import { doc, onSnapshot, setDoc } from 'firebase/firestore'
import { AnalysisMode, CONFIG, IndexSymbol, TimeGap } from '../config/constants'

interface SettingsState {
    symbol: IndexSymbol
    expiry: string | null
    range: number // Strike Range
    mode: AnalysisMode // OI or Volume
    isLive: boolean
    historicalDate: string | null
    datasetDate: string | null
    timeGap: TimeGap
    simulationTime: string // HH:mm
    rowsPerPage: number
    baseTime: string // HH:mm
    // Compatibility fields with UserSettings
    dataSource: 'simulation' | 'api' | 'upstox' | 'database'
    enableCloudBackup: boolean
    apiEndpoint?: string
    theme?: 'light' | 'dark'
    // Combined Metrics Section (persisted)
    combinedMetricType: 'OI' | 'Volume'
    combinedViewMode: 'snapshot' | 'comparison'
    subTab: string
    // Inspector specific
    inspectionSource: 'CurrentIntraday' | 'HistoricalSnapshot' | 'RangeIntraday' | 'RangeEOD' | 'OPFSDebug'
    rangeStartDate: string
    rangeEndDate: string
    lookbackMinutes: number // Time gap for % change comparison in Intraday History
    isSlicerSticky: boolean // Whether the TimeRangeSlicer is sticky on desktop/tablet

    // Actions
    setSymbol: (symbol: IndexSymbol) => void
    setExpiry: (expiry: string) => void
    setRange: (range: number) => void
    setMode: (mode: AnalysisMode) => void
    setIsLive: (isLive: boolean) => void
    setHistoricalDate: (date: string | null) => void
    setDatasetDate: (date: string | null) => void
    setTheme: (theme: 'light' | 'dark') => void
    setTimeGap: (gap: TimeGap) => void
    setSimulationTime: (time: string) => void
    setRowsPerPage: (rows: number) => void
    setBaseTime: (time: string) => void
    executionMode: 'PROD' | 'BETA'
    setExecutionMode: (mode: 'PROD' | 'BETA') => void
    setCombinedMetricType: (type: 'OI' | 'Volume') => void
    setCombinedViewMode: (mode: 'snapshot' | 'comparison') => void
    setSubTab: (tab: string) => void
    setIsSlicerSticky: (isSticky: boolean) => void
    setInspectionSource: (
        source: 'CurrentIntraday' | 'HistoricalSnapshot' | 'RangeIntraday' | 'RangeEOD' | 'OPFSDebug',
    ) => void
    setRangeStartDate: (date: string) => void
    setRangeEndDate: (date: string) => void
    setLookbackMinutes: (minutes: number) => void
}

export const useSettingsStore = create<SettingsState>()(
    persist(
        (set, get) => ({
            symbol: IndexSymbol.NIFTY, // Default
            expiry: null,
            range: 10,
            mode: AnalysisMode.OI,
            isLive: true,
            theme: 'light',
            historicalDate: null,
            datasetDate: null,
            timeGap: TimeGap.M5,
            simulationTime: '15:30',
            baseTime: '09:16',
            rowsPerPage: CONFIG.UI.DEFAULT_ROWS_PER_PAGE,
            dataSource: 'upstox',
            enableCloudBackup: false,
            combinedMetricType: 'OI',
            combinedViewMode: 'snapshot',
            subTab: 'Combined OI',
            inspectionSource: 'CurrentIntraday',
            rangeStartDate: (() => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().split('T')[0] || '' })(), // Today
            rangeEndDate: (() => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().split('T')[0] || '' })(), // Today
            lookbackMinutes: 15,

            setSymbol: (symbol) => set({ symbol }),
            setExpiry: (expiry) => set({ expiry }),
            setRange: (range) => set({ range }),
            setMode: (mode) => set({ mode }),
            setIsLive: (isLive) =>
                set((state: any) => {
                    const newHistoricalDate = isLive ? null : state.historicalDate
                    const newSource = isLive
                        ? 'CurrentIntraday'
                        : newHistoricalDate
                          ? 'HistoricalSnapshot'
                          : 'CurrentIntraday'
                    return {
                        isLive,
                        historicalDate: newHistoricalDate,
                        inspectionSource: newSource,
                        // Reset time slicer to full day when switching to Historical
                        ...(!isLive ? { simulationTime: '15:30' } : {}),
                    }
                }),
            setHistoricalDate: (date) =>
                set((state: any) => {
                    const newSource = state.isLive
                        ? 'CurrentIntraday'
                        : date
                          ? 'HistoricalSnapshot'
                          : 'CurrentIntraday'
                    return {
                        historicalDate: date,
                        isLive: date ? false : true,
                        inspectionSource: newSource,
                        // Reset time slicer to full day when entering Historical mode
                        ...(date ? { simulationTime: '15:30' } : {}),
                    }
                }),
            setDatasetDate: (date) => set({ datasetDate: date }),
            setTheme: (theme: 'light' | 'dark') => set({ theme }),
            setTimeGap: (gap) => set({ timeGap: gap }),
            setSimulationTime: (time) => set({ simulationTime: time }),
            setBaseTime: (time) => set({ baseTime: time }),
            setRowsPerPage: (rows) => set({ rowsPerPage: rows }),
            setDataSource: (source: any) => set({ dataSource: source }),
            setEnableCloudBackup: (enable: any) => set({ enableCloudBackup: enable }),
            executionMode: 'PROD',
            setExecutionMode: (mode: 'PROD' | 'BETA') => set({ executionMode: mode }),
            setCombinedMetricType: (type) => set({ combinedMetricType: type }),
            setCombinedViewMode: (mode) => set({ combinedViewMode: mode }),
            setSubTab: (tab) => set({ subTab: tab }),
            setIsSlicerSticky: (isSticky) => set({ isSlicerSticky: isSticky }),
            setInspectionSource: (source: any) =>
                set((state: any) => {
                    if (state.inspectionSource !== source) {
                        return { inspectionSource: source }
                    }
                    return {}
                }),
            setRangeStartDate: (date) => set({ rangeStartDate: date }),
            setRangeEndDate: (date) => set({ rangeEndDate: date }),
            setLookbackMinutes: (minutes) => set({ lookbackMinutes: minutes }),
            _setIsSyncingFromCloud: (val: boolean) => set({ isSyncingFromCloud: val }),
            initializeCloudSync: () => {
                const auth = getFirebaseAuth();
                const db = getFirestore();
                
                // Wait for auth to settle
                setTimeout(() => {
                    const currentUser = auth.currentUser;
                    if (currentUser) {
                        const docRef = doc(db, 'user_settings', currentUser.uid);
                        onSnapshot(docRef, (docSnap: any) => {
                            if (docSnap.exists()) {
                                const cloudData = docSnap.data();
                                // Set flag to prevent infinite loop
                                get()._setIsSyncingFromCloud(true);
                                set((state: any) => ({ ...state, ...cloudData }));
                                setTimeout(() => get()._setIsSyncingFromCloud(false), 50);
                            } else {
                                // If the document doesn't exist on Firebase yet, upload our local settings!
                                const { isSyncingFromCloud, _setIsSyncingFromCloud, initializeCloudSync, ...localDataToSync } = get();
                                setDoc(docRef, localDataToSync, { merge: true }).catch(err => console.error("[AsyncCatch] Operation failed:", err));
                            }
                        });
                    }
                }, 1000);
            }
        }),
        {
            name: 'oms-settings-v1', // unique name for local storage
            version: 2,
            migrate: (persisted: any) => ({
                ...persisted,
                executionMode: 'PROD',
            }),
            // Only persist data fields, NOT actions/functions
            partialize: (state: any) => ({
                symbol: state.symbol,
                expiry: state.expiry,
                range: state.range,
                mode: state.mode,
                isLive: state.isLive,
                historicalDate: state.historicalDate,
                datasetDate: state.datasetDate,
                timeGap: state.timeGap,
                simulationTime: state.simulationTime,
                rowsPerPage: state.rowsPerPage,
                baseTime: state.baseTime,
                dataSource: state.dataSource,
                enableCloudBackup: state.enableCloudBackup,
                apiEndpoint: state.apiEndpoint,
                theme: state.theme,
                combinedMetricType: state.combinedMetricType,
                combinedViewMode: state.combinedViewMode,
                subTab: state.subTab,
                inspectionSource: state.inspectionSource,
                rangeStartDate: state.rangeStartDate,
                rangeEndDate: state.rangeEndDate,
                lookbackMinutes: state.lookbackMinutes,
                isSlicerSticky: state.isSlicerSticky,
                executionMode: state.executionMode,
            }),
        },
    ),
)

// Setup global subscriber to push changes to cloud
if (typeof window !== 'undefined') {
    let debounceTimer: any = null;
    useSettingsStore.subscribe((state: any, prevState: any) => {
        if (state.isSyncingFromCloud) return; // Prevent echoing back to cloud
        // Only sync data fields
        const dataToSync = {
            symbol: state.symbol,
            expiry: state.expiry,
            range: state.range,
            mode: state.mode,
            isLive: state.isLive,
            historicalDate: state.historicalDate,
            datasetDate: state.datasetDate,
            timeGap: state.timeGap,
            simulationTime: state.simulationTime,
            rowsPerPage: state.rowsPerPage,
            baseTime: state.baseTime,
            dataSource: state.dataSource,
            enableCloudBackup: state.enableCloudBackup,
            apiEndpoint: state.apiEndpoint,
            theme: state.theme,
            combinedMetricType: state.combinedMetricType,
            combinedViewMode: state.combinedViewMode,
            subTab: state.subTab,
            inspectionSource: state.inspectionSource,
            rangeStartDate: state.rangeStartDate,
            rangeEndDate: state.rangeEndDate,
            lookbackMinutes: state.lookbackMinutes,
            isSlicerSticky: state.isSlicerSticky,
            executionMode: state.executionMode,
        };
        
        // Compare to prevent unnecessary writes
        const isChanged = Object.keys(dataToSync).some(key => (dataToSync as any)[key] !== prevState[key]);
        if (!isChanged) return;

        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
            try {
                const auth = getFirebaseAuth();
                const db = getFirestore();
                if (auth.currentUser) {
                    setDoc(doc(db, 'user_settings', auth.currentUser.uid), dataToSync, { merge: true }).catch(err => console.error("[AsyncCatch] Operation failed:", err));
                }
            } catch (e) { console.warn('Ignored error in settingsStore.ts:', e); }
        }, 1000); // 1s debounce to save Firestore writes
    });
}
